import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Search,
  User,
  Eye,
  X,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  CreditCard,
  Image,
  ZoomIn,
  Loader2,
  Building2,
  Wallet
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useApp, PAKISTAN_BANKS } from '../../context/AppContext';
import { useBrokerData } from '../../context/BrokerDataContext';
import { getBrokerFromPath } from '../../config/brokerConfig';
import './UserManagement.css';

function UserManagement() {
  const location = useLocation();
  const brokerId = getBrokerFromPath(location.pathname);

  // Get broker-specific data from BrokerDataContext
  const {
    transactions,
    whitelistedAccounts,
    pendingRegistrations: pendingAccountRegistrations,
    isLoading
  } = useBrokerData();

  // Get broker users from AppContext (filtered by broker)
  const { brokerUsers: allBrokerUsers } = useApp();

  // Filter broker users to only show users for the current broker
  const brokerName = brokerId === 'justmarkets' ? 'JustMarkets' : 'Savexa';
  const brokerUsers = allBrokerUsers.filter(user => user.broker === brokerName);

  const [searchTerm, setSearchTerm] = useState('');
  const [brokerFilter, setBrokerFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null); // For image zoom modal

  // Filter users
  const filteredUsers = brokerUsers
    .filter(user => {
      const matchesSearch =
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.cnic.includes(searchTerm);

      const matchesBroker =
        brokerFilter === 'all' || user.broker === brokerFilter;

      return matchesSearch && matchesBroker;
    })
    // Sort by date - newest first
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  // Get user transaction stats
  const getUserStats = (userId) => {
    const userTransactions = transactions.filter(t => t.userId === userId);
    return {
      totalTransactions: userTransactions.length,
      completedTransactions: userTransactions.filter(t => t.status === 'completed').length,
      pendingTransactions: userTransactions.filter(t => t.status === 'pending_review').length,
      totalAmount: userTransactions
        .filter(t => t.status === 'completed')
        .reduce((sum, t) => sum + (t.amount || 0), 0),
    };
  };

  // Get user's bank accounts (approved, rejected, pending)
  const getUserAccounts = (cnic) => {
    const approved = whitelistedAccounts?.filter(acc => acc.cnic === cnic && acc.status === 'whitelisted') || [];
    const rejected = whitelistedAccounts?.filter(acc => acc.cnic === cnic && acc.status === 'rejected') || [];
    const pending = pendingAccountRegistrations?.filter(acc => acc.cnic === cnic) || [];
    return { approved, rejected, pending };
  };

  // Get bank logo
  const getBankLogo = (bankCode) => {
    const bank = PAKISTAN_BANKS.find(b => b.code === bankCode);
    const isWallet = bank?.type === 'wallet';
    const logoUrl = bank?.logo;

    return (
      <div className="bank-logo-wrapper">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={bank?.name || bankCode}
            className="bank-logo"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="bank-logo-fallback" style={{ display: logoUrl ? 'none' : 'flex' }}>
          {isWallet ? <Wallet size={18} /> : <Building2 size={18} />}
        </div>
      </div>
    );
  };

  // Get unique brokers for filter
  const uniqueBrokers = [...new Set(brokerUsers.map(u => u.broker))];

  // Select user handler
  const handleSelectUser = (user, stats) => {
    setSelectedUser({ ...user, stats });
  };

  // Close modal handler
  const handleCloseModal = () => {
    setSelectedUser(null);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="user-management">
        <div className="loading-container">
          <Loader2 size={48} className="spinning" />
          <h3>Loading Users...</h3>
          <p>Fetching user data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      {/* Header */}
      <div className="page-header">
        <h1>User Management</h1>
        <p>View and manage broker users</p>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <CardContent>
          <div className="filters-row">
            <div className="search-wrapper">
              <Input
                placeholder="Search by name or CNIC..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={Search}
              />
            </div>

            <div className="broker-filter">
              <select
                value={brokerFilter}
                onChange={(e) => setBrokerFilter(e.target.value)}
                className="select"
              >
                <option value="all">All Brokers</option>
                {uniqueBrokers.map(broker => (
                  <option key={broker} value={broker}>{broker}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="users-table-card">
        <CardContent>
          <div className="users-table">
            <div className="users-table-header">
              <span>Name</span>
              <span>CNIC</span>
              <span>Phone</span>
              <span>Transactions</span>
              <span>Volume</span>
              <span>Action</span>
            </div>
            {filteredUsers.map(user => {
              const stats = getUserStats(user.id);
              return (
                <div key={user.id} className="users-table-row">
                  <span className="user-name">{user.name}</span>
                  <span className="user-cnic">{user.cnic}</span>
                  <span className="user-phone">{user.phone}</span>
                  <span className="user-txns">{stats.totalTransactions}</span>
                  <span className="user-volume">PKR {stats.totalAmount.toLocaleString()}</span>
                  <button
                    className="view-btn"
                    onClick={() => handleSelectUser(user, stats)}
                  >
                    <Eye size={16} />
                    View
                  </button>
                </div>
              );
            })}
            {filteredUsers.length === 0 && (
              <div className="empty-state">
                <User size={48} />
                <h3>No users found</h3>
                <p>No users match your search criteria</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {/* User Information */}
              <div className="detail-section">
                <h4>Personal Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Name</span>
                    <span className="value">{selectedUser.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">CNIC No</span>
                    <span className="value">{selectedUser.cnic}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Address</span>
                    <span className="value">{selectedUser.address || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Expiry Date</span>
                    <span className="value">{selectedUser.cnicExpiry || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* CNIC Images */}
              <div className="detail-section">
                <h4>CNIC Documents</h4>
                <div className="cnic-images-grid">
                  <div className="cnic-image-box">
                    <div className="cnic-image-label">
                      <Image size={14} />
                      <span>CNIC Front</span>
                    </div>
                    <div className="cnic-image-container">
                      {selectedUser.cnicFrontImage ? (
                        <>
                          <img
                            src={selectedUser.cnicFrontImage}
                            alt="CNIC Front"
                            className="cnic-image"
                            onClick={() => setZoomedImage({ src: selectedUser.cnicFrontImage, title: 'CNIC Front' })}
                          />
                          <div className="cnic-zoom-hint">
                            <ZoomIn size={16} />
                          </div>
                        </>
                      ) : (
                        <div className="cnic-image-placeholder">
                          <Image size={32} />
                          <span>No image uploaded</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="cnic-image-box">
                    <div className="cnic-image-label">
                      <Image size={14} />
                      <span>CNIC Back</span>
                    </div>
                    <div className="cnic-image-container">
                      {selectedUser.cnicBackImage ? (
                        <>
                          <img
                            src={selectedUser.cnicBackImage}
                            alt="CNIC Back"
                            className="cnic-image"
                            onClick={() => setZoomedImage({ src: selectedUser.cnicBackImage, title: 'CNIC Back' })}
                          />
                          <div className="cnic-zoom-hint">
                            <ZoomIn size={16} />
                          </div>
                        </>
                      ) : (
                        <div className="cnic-image-placeholder">
                          <Image size={32} />
                          <span>No image uploaded</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Transaction Statistics */}
              <div className="detail-section">
                <h4>Transaction Statistics</h4>
                <div className="stats-grid">
                  <div className="stat-box">
                    <FileText size={20} />
                    <div className="stat-info">
                      <span className="stat-value">{selectedUser.stats.totalTransactions}</span>
                      <span className="stat-label">Total Transactions</span>
                    </div>
                  </div>
                  <div className="stat-box completed">
                    <CheckCircle size={20} />
                    <div className="stat-info">
                      <span className="stat-value">{selectedUser.stats.completedTransactions}</span>
                      <span className="stat-label">Completed</span>
                    </div>
                  </div>
                  <div className="stat-box pending">
                    <Clock size={20} />
                    <div className="stat-info">
                      <span className="stat-value">{selectedUser.stats.pendingTransactions}</span>
                      <span className="stat-label">Pending</span>
                    </div>
                  </div>
                </div>
                <div className="total-volume">
                  <span className="label">Total Volume (Completed)</span>
                  <span className="value">PKR {selectedUser.stats.totalAmount.toLocaleString()}</span>
                </div>
              </div>

              {/* Bank Accounts */}
              {(() => {
                const accounts = getUserAccounts(selectedUser.cnic);
                const hasAccounts = accounts.approved.length > 0 || accounts.rejected.length > 0 || accounts.pending.length > 0;

                return (
                  <div className="detail-section">
                    <h4>Bank Accounts</h4>
                    {!hasAccounts ? (
                      <p className="no-accounts">No bank accounts registered</p>
                    ) : (
                      <div className="accounts-list-modal">
                        {accounts.approved.map(acc => (
                          <div key={acc.id} className="account-item approved">
                            <div className="account-icon">
                              {getBankLogo(acc.bankCode)}
                            </div>
                            <div className="account-details">
                              <span className="account-bank">{acc.bankName}</span>
                              <span className="account-number">{acc.iban || acc.accountNumber}</span>
                            </div>
                            <span className="account-status approved">
                              <CheckCircle size={14} />
                              Approved
                            </span>
                          </div>
                        ))}
                        {accounts.pending.map(acc => (
                          <div key={acc.id} className="account-item pending">
                            <div className="account-icon">
                              {getBankLogo(acc.bankCode)}
                            </div>
                            <div className="account-details">
                              <span className="account-bank">{acc.bankName}</span>
                              <span className="account-number">{acc.iban || acc.accountNumber}</span>
                            </div>
                            <span className="account-status pending">
                              <Clock size={14} />
                              Pending
                            </span>
                          </div>
                        ))}
                        {accounts.rejected.map(acc => (
                          <div key={acc.id} className="account-item rejected">
                            <div className="account-icon">
                              {getBankLogo(acc.bankCode)}
                            </div>
                            <div className="account-details">
                              <span className="account-bank">{acc.bankName}</span>
                              <span className="account-number">{acc.iban || acc.accountNumber}</span>
                              {acc.rejectionReason && (
                                <span className="rejection-reason">Reason: {acc.rejectionReason}</span>
                              )}
                            </div>
                            <span className="account-status rejected">
                              <XCircle size={14} />
                              Rejected
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Recent Transactions */}
              <div className="detail-section">
                <h4>Recent Transactions</h4>
                <div className="recent-transactions">
                  {transactions
                    .filter(t => t.userId === selectedUser.id)
                    .slice(0, 5)
                    .map(t => (
                      <div key={t.id} className="transaction-row">
                        <div className="txn-info">
                          <span className="txn-ref">{t.referenceNumber}</span>
                          <span className="txn-date">
                            {new Date(t.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="txn-amount">
                          PKR {(t.amount || 0).toLocaleString()}
                        </div>
                        <span className={`txn-status ${t.status}`}>
                          {t.status === 'completed' ? 'Completed' :
                           t.status === 'pending_review' ? 'Pending' : t.status}
                        </span>
                      </div>
                    ))}
                  {transactions.filter(t => t.userId === selectedUser.id).length === 0 && (
                    <p className="no-transactions">No transactions yet</p>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <Button variant="secondary" onClick={handleCloseModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div className="image-zoom-overlay" onClick={() => setZoomedImage(null)}>
          <div className="image-zoom-container" onClick={(e) => e.stopPropagation()}>
            <div className="image-zoom-header">
              <h3>{zoomedImage.title}</h3>
              <button className="image-zoom-close" onClick={() => setZoomedImage(null)}>
                <X size={24} />
              </button>
            </div>
            <div className="image-zoom-content">
              <img src={zoomedImage.src} alt={zoomedImage.title} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
